/**
 * LMSAdminCourseBuilderPage.jsx — Admin Course Builder.
 *
 * Premium course authoring console for super_admin / org_admin / event_manager.
 * - List of all courses (left rail)
 * - Editor (centre): metadata, modules + lessons, premium toggle
 * - Publish / Preview
 *
 * Uses /api/lms/admin/course-builder/{list,save,publish,preview}.
 */
import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  BookOpen, Plus, Save, Eye, Sparkles, GraduationCap, Trash2,
  ChevronUp, ChevronDown, ListChecks, Crown,
} from "lucide-react";
import { Hero, Loading, EmptyState } from "./LMSTrainerDashboardPage";

const EMPTY_COURSE = {
  id: null,
  title: "",
  subtitle: "",
  description: "",
  level: "beginner",
  skill_outcomes: [],
  thumbnail_url: "",
  estimated_duration_min: 60,
  premium_diploks_required: false,
  is_published: false,
  modules: [],
  certificate_rule: { min_lessons_pct: 80, min_quiz_avg: 70 },
};

export default function LMSAdminCourseBuilderPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(EMPTY_COURSE);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/lms/admin/course-builder/list");
      setCourses(r.data.courses || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const newCourse = () => setActive({ ...EMPTY_COURSE });

  const save = async () => {
    if (!active.title?.trim()) {
      toast.error("Course title required.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/lms/admin/course-builder/save", active);
      toast.success("Course saved");
      setActive(r.data);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const publish = async (flag) => {
    if (!active.id) { toast.error("Save the course first."); return; }
    try {
      const r = await api.post(
        `/lms/admin/course-builder/publish?course_id=${active.id}`,
        { is_published: flag },
      );
      setActive(r.data);
      toast.success(flag ? "Course published" : "Course unpublished");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addModule = () => {
    const mods = [...(active.modules || [])];
    mods.push({
      id: `mod-${Date.now()}`,
      title: `Module ${mods.length + 1}`,
      order: mods.length + 1,
      lessons: [],
    });
    setActive({ ...active, modules: mods });
  };

  const updateModule = (idx, patch) => {
    const mods = [...(active.modules || [])];
    mods[idx] = { ...mods[idx], ...patch };
    setActive({ ...active, modules: mods });
  };

  const moveModule = (idx, dir) => {
    const mods = [...(active.modules || [])];
    const t = idx + dir;
    if (t < 0 || t >= mods.length) return;
    [mods[idx], mods[t]] = [mods[t], mods[idx]];
    mods.forEach((m, i) => { m.order = i + 1; });
    setActive({ ...active, modules: mods });
  };

  const removeModule = (idx) => {
    const mods = [...(active.modules || [])];
    mods.splice(idx, 1);
    mods.forEach((m, i) => { m.order = i + 1; });
    setActive({ ...active, modules: mods });
  };

  const addLesson = (mIdx) => {
    const mods = [...(active.modules || [])];
    const lessons = [...(mods[mIdx].lessons || [])];
    lessons.push({
      lesson_id: `les-${Date.now()}`,
      title: `Lesson ${lessons.length + 1}`,
      video_url: "",
      required_watch_percent: 75,
      mandatory: true,
      order: lessons.length + 1,
    });
    mods[mIdx] = { ...mods[mIdx], lessons };
    setActive({ ...active, modules: mods });
  };

  const updateLesson = (mIdx, lIdx, patch) => {
    const mods = [...(active.modules || [])];
    const lessons = [...(mods[mIdx].lessons || [])];
    lessons[lIdx] = { ...lessons[lIdx], ...patch };
    mods[mIdx] = { ...mods[mIdx], lessons };
    setActive({ ...active, modules: mods });
  };

  const removeLesson = (mIdx, lIdx) => {
    const mods = [...(active.modules || [])];
    const lessons = [...(mods[mIdx].lessons || [])];
    lessons.splice(lIdx, 1);
    mods[mIdx] = { ...mods[mIdx], lessons };
    setActive({ ...active, modules: mods });
  };

  return (
    <DashboardLayout title="Course Builder">
      <div data-testid="lmsx-course-builder" className="space-y-6 pb-12">
        <Hero
          eyebrow="Admin · Diplomexa Academy"
          title="Authoring console for premium courses"
          subtitle="Compose modules, lessons, and certificate rules. Toggle Diploks-premium courses with one click."
        />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          {/* Left rail: course list */}
          <aside className="lg:col-span-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0F4C81]">
                All Courses
              </p>
              <button
                type="button"
                onClick={newCourse}
                data-testid="lmsx-builder-new"
                className="inline-flex items-center gap-1 rounded-md border border-[#0F4C81]/40 px-2.5 py-1 text-[11px] font-semibold text-[#0F4C81] hover:bg-blue-50"
              >
                <Plus size={11} /> New
              </button>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm space-y-1 max-h-[500px] overflow-auto">
              {loading ? <Loading /> : courses.length === 0 ? (
                <EmptyState label="No courses yet." />
              ) : (
                courses.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setActive(c)}
                    data-testid={`lmsx-builder-course-${c.id}`}
                    className={`w-full text-left rounded-lg border px-3 py-2 ${active?.id === c.id ? "border-[#D4AF37] bg-amber-50/40" : "border-slate-200 hover:border-[#0F4C81]/40"}`}
                  >
                    <p className="text-sm font-semibold text-[#0A192F] truncate">{c.title}</p>
                    <div className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-500">
                      <span className="capitalize">{c.level}</span>
                      <span>·</span>
                      <span className={c.is_published ? "text-emerald-700 font-bold" : "text-amber-700 font-bold"}>
                        {c.is_published ? "PUBLISHED" : "DRAFT"}
                      </span>
                      {c.premium_diploks_required ? (
                        <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-[#0A192F] px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#D4AF37]">
                          <Crown size={9} /> Premium
                        </span>
                      ) : null}
                    </div>
                  </button>
                ))
              )}
            </div>
          </aside>

          {/* Editor */}
          <main className="lg:col-span-8 space-y-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
                  <BookOpen size={14} className="text-[#D4AF37]" /> Course details
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid="lmsx-builder-save"
                    className="inline-flex items-center gap-1 rounded-lg bg-[#0A192F] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-[#0F2C50] disabled:opacity-50"
                  >
                    <Save size={11} /> {saving ? "Saving…" : "Save"}
                  </button>
                  {active.is_published ? (
                    <button
                      type="button"
                      onClick={() => publish(false)}
                      data-testid="lmsx-builder-unpublish"
                      className="inline-flex items-center gap-1 rounded-lg border border-amber-300 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-800 hover:bg-amber-50"
                    >
                      <Eye size={11} /> Unpublish
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => publish(true)}
                      data-testid="lmsx-builder-publish"
                      className="inline-flex items-center gap-1 rounded-lg bg-[#D4AF37] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A192F] hover:bg-[#c69f30]"
                    >
                      <Sparkles size={11} /> Publish
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <Field label="Title">
                  <input
                    value={active.title || ""}
                    onChange={(e) => setActive({ ...active, title: e.target.value })}
                    data-testid="lmsx-builder-title"
                    className={fieldClass}
                  />
                </Field>
                <Field label="Subtitle">
                  <input
                    value={active.subtitle || ""}
                    onChange={(e) => setActive({ ...active, subtitle: e.target.value })}
                    data-testid="lmsx-builder-subtitle"
                    className={fieldClass}
                  />
                </Field>
                <Field label="Level">
                  <select
                    value={active.level || "beginner"}
                    onChange={(e) => setActive({ ...active, level: e.target.value })}
                    data-testid="lmsx-builder-level"
                    className={fieldClass}
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                    <option value="masterclass">Masterclass</option>
                  </select>
                </Field>
                <Field label="Estimated duration (min)">
                  <input
                    type="number"
                    value={active.estimated_duration_min || 60}
                    onChange={(e) => setActive({ ...active, estimated_duration_min: parseInt(e.target.value || 0, 10) })}
                    data-testid="lmsx-builder-duration"
                    className={fieldClass}
                  />
                </Field>
                <Field label="Thumbnail URL" full>
                  <input
                    value={active.thumbnail_url || ""}
                    onChange={(e) => setActive({ ...active, thumbnail_url: e.target.value })}
                    data-testid="lmsx-builder-thumbnail"
                    className={fieldClass}
                  />
                </Field>
                <Field label="Skill outcomes (comma-separated)" full>
                  <input
                    value={(active.skill_outcomes || []).join(", ")}
                    onChange={(e) => setActive({ ...active, skill_outcomes: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                    data-testid="lmsx-builder-skills"
                    className={fieldClass}
                    placeholder="research, diplomacy, public-speaking"
                  />
                </Field>
                <Field label="Description" full>
                  <textarea
                    value={active.description || ""}
                    onChange={(e) => setActive({ ...active, description: e.target.value })}
                    data-testid="lmsx-builder-description"
                    className={`${fieldClass} min-h-[100px]`}
                  />
                </Field>
              </div>

              <label className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!active.premium_diploks_required}
                  onChange={(e) => setActive({ ...active, premium_diploks_required: e.target.checked })}
                  data-testid="lmsx-builder-premium-toggle"
                />
                <span className="text-xs font-semibold text-[#0A192F] inline-flex items-center gap-1">
                  <Crown size={12} className="text-[#D4AF37]" /> Premium course — requires Diploks to enroll
                </span>
              </label>
            </div>

            {/* Modules */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
                  <ListChecks size={14} className="text-[#D4AF37]" /> Modules & Lessons
                </h3>
                <button
                  type="button"
                  onClick={addModule}
                  data-testid="lmsx-builder-add-module"
                  className="inline-flex items-center gap-1 rounded-md border border-[#0F4C81]/40 px-3 py-1.5 text-[11px] font-semibold text-[#0F4C81] hover:bg-blue-50"
                >
                  <Plus size={11} /> Add module
                </button>
              </div>
              <div className="mt-3 space-y-3">
                {(active.modules || []).length === 0 ? (
                  <EmptyState label="Add your first module to start composing lessons." />
                ) : (
                  (active.modules || []).map((m, mIdx) => (
                    <div key={m.id || mIdx}
                          data-testid={`lmsx-builder-module-${mIdx}`}
                          className="rounded-xl border border-slate-200 bg-slate-50/50 p-3">
                      <div className="flex items-center gap-2">
                        <input
                          value={m.title || ""}
                          onChange={(e) => updateModule(mIdx, { title: e.target.value })}
                          data-testid={`lmsx-builder-module-title-${mIdx}`}
                          className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-[#0A192F]"
                          placeholder={`Module ${mIdx + 1}`}
                        />
                        <button onClick={() => moveModule(mIdx, -1)} disabled={mIdx === 0}
                                className="rounded-md border border-slate-200 p-1.5 hover:border-[#0F4C81]/40 disabled:opacity-40">
                          <ChevronUp size={12} />
                        </button>
                        <button onClick={() => moveModule(mIdx, +1)} disabled={mIdx === (active.modules.length - 1)}
                                className="rounded-md border border-slate-200 p-1.5 hover:border-[#0F4C81]/40 disabled:opacity-40">
                          <ChevronDown size={12} />
                        </button>
                        <button onClick={() => removeModule(mIdx)}
                                data-testid={`lmsx-builder-module-delete-${mIdx}`}
                                className="rounded-md border border-rose-200 p-1.5 text-rose-600 hover:bg-rose-50">
                          <Trash2 size={12} />
                        </button>
                      </div>
                      <div className="mt-3 space-y-2">
                        {(m.lessons || []).map((l, lIdx) => (
                          <div key={l.lesson_id || lIdx}
                                data-testid={`lmsx-builder-lesson-${mIdx}-${lIdx}`}
                                className="grid grid-cols-1 gap-2 rounded-lg border border-slate-200 bg-white p-2 md:grid-cols-12">
                            <input
                              value={l.title || ""}
                              onChange={(e) => updateLesson(mIdx, lIdx, { title: e.target.value })}
                              data-testid={`lmsx-builder-lesson-title-${mIdx}-${lIdx}`}
                              placeholder="Lesson title"
                              className="md:col-span-4 rounded-md border border-slate-200 px-2 py-1.5 text-xs"
                            />
                            <input
                              value={l.video_url || ""}
                              onChange={(e) => updateLesson(mIdx, lIdx, { video_url: e.target.value })}
                              data-testid={`lmsx-builder-lesson-url-${mIdx}-${lIdx}`}
                              placeholder="Video URL"
                              className="md:col-span-5 rounded-md border border-slate-200 px-2 py-1.5 text-xs"
                            />
                            <input
                              type="number" min={0} max={100}
                              value={l.required_watch_percent ?? 75}
                              onChange={(e) => updateLesson(mIdx, lIdx, { required_watch_percent: parseInt(e.target.value || 0, 10) })}
                              data-testid={`lmsx-builder-lesson-watch-${mIdx}-${lIdx}`}
                              placeholder="% req"
                              className="md:col-span-2 rounded-md border border-slate-200 px-2 py-1.5 text-xs text-center"
                            />
                            <button
                              type="button"
                              onClick={() => removeLesson(mIdx, lIdx)}
                              data-testid={`lmsx-builder-lesson-delete-${mIdx}-${lIdx}`}
                              className="md:col-span-1 rounded-md border border-rose-200 p-1.5 text-rose-600 hover:bg-rose-50 inline-flex items-center justify-center"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addLesson(mIdx)}
                          data-testid={`lmsx-builder-add-lesson-${mIdx}`}
                          className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-3 py-1.5 text-[11px] text-slate-600 hover:border-[#0F4C81]/40 hover:text-[#0F4C81]"
                        >
                          <Plus size={11} /> Add lesson
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </DashboardLayout>
  );
}

const fieldClass = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-[#0A192F] focus:border-[#0F4C81] focus:ring-2 focus:ring-[#0F4C81]/15";

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-600">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
